// src/features/billing/LegalEntityCard.tsx
// Links a billing account to the LegalEntity it represents (AttachLegalEntityToBillingAccount).
// One billing account represents one legal entity; one legal entity may own
// multiple billing accounts (per currency, per product line). Contract
// rendering resolves full bank requisites through this link.
//
// The `legalentity` domain has no frontend of its own yet (0% UI coverage),
// so this takes a legal entity ID directly rather than linking to a picker —
// same approach as payment's "linked legal entity" field on a payment method.

import { useState } from 'react';
import { observer } from '@react';
import { useBillingBillingAccount, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardContent, Input, ErrorAlert } from '@react/components';
import type { Entity } from '@core/types/entity';
import { errMessage } from './format';

export interface LegalEntityCardProps {
  billingAccountId: string;
  legalEntity?: Entity;
  onAttached?: () => void;
}

export const LegalEntityCard = observer(({ billingAccountId, legalEntity, onAttached }: LegalEntityCardProps) => {
  const store = useBillingBillingAccount();
  const { t } = useI18n();
  const [legalEntityId, setLegalEntityId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAttach = async () => {
    if (!legalEntityId.trim()) return;
    setError(null);
    try {
      await store.AttachLegalEntityToBillingAccount(billingAccountId, {
        billing_account: { id: billingAccountId } as any,
        legal_entity: { id: legalEntityId.trim() } as any,
      });
      setLegalEntityId('');
      onAttached?.();
    } catch (err) {
      setError(errMessage(err, t('billing.legalEntity.attachError')));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('billing.legalEntity.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? <ErrorAlert error={error} /> : null}
        {legalEntity?.id ? (
          <p className="text-sm text-foreground">
            {t('billing.legalEntity.attached')}: <span className="font-mono text-muted-foreground">{legalEntity.id}</span>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">{t('billing.legalEntity.none')}</p>
        )}
        <div className="flex gap-2">
          <Input
            value={legalEntityId}
            onChange={(e) => setLegalEntityId(e.target.value)}
            placeholder={t('billing.legalEntity.idPlaceholder')}
            className="flex-1"
            fullWidth={false}
          />
          <Button type="button" variant="secondary" size="sm" onClick={handleAttach} isLoading={store.isLoading} disabled={!legalEntityId.trim()}>
            {legalEntity?.id ? t('billing.legalEntity.replace') : t('billing.legalEntity.attach')}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t('billing.legalEntity.hint')}</p>
      </CardContent>
    </Card>
  );
});

export default LegalEntityCard;
